import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { recordPageVisit } from "@/lib/api";
import { 
  Heart, 
  Phone, 
  Globe, 
  Sparkles,
  HandHeart,
  Users,
  FileText,
  Shield,
  CheckCircle,
  ExternalLink
} from "lucide-react";

// Programs data with translation keys
const getPrograms = (t) => [
  {
    id: "massage-therapy",
    title: t("programs.massage.title", "End-of-Life Massage Therapy"),
    subtitle: t("programs.massage.subtitle", "Comfort Through Healing Touch"),
    icon: HandHeart,
    color: "bg-pink-100 text-pink-700",
    description: t("programs.massage.description", "Physical touch can have a profound impact on the overall wellbeing of someone who is dealing with serious medical issues. Research indicates that massage therapy can improve the quality of life for those receiving hospice care."),
    quote: t("programs.massage.quote", "One comment we hear most often from patients is how much they enjoyed the company of our therapists. They can benefit from the social interaction as well as the therapeutic massage."),
    quoteAuthor: t("programs.massage.quoteAuthor", "Laura Bryant-Earner, Service Director at Healwell"),
    benefits: [
      t("programs.massage.benefits.1", "Improves quality of life for hospice patients"),
      t("programs.massage.benefits.2", "Reduces anxiety and stress levels"),
      t("programs.massage.benefits.3", "Alleviates pain, loss of sleep, nausea, and fatigue"),
      t("programs.massage.benefits.4", "Helps muscles relax and release tension"),
      t("programs.massage.benefits.5", "Provides comforting social interaction"),
      t("programs.massage.benefits.6", "Promotes feelings of peace and dignity")
    ],
    details: t("programs.massage.details", "Anyone receiving care from Goodwin Hospice can request massage therapy, and they will receive up to three sessions, which can be shared between patients and family members."),
    highlight: t("programs.massage.highlight", "Offered at no additional cost to patients"),
    phone: "703.578.7108",
    website: "https://GoodwinHospice.org"
  },
  {
    id: "doula-services",
    title: t("programs.doula.title", "End-of-Life Doula Services"),
    subtitle: t("programs.doula.subtitle", "Compassionate Guidance Through Transition"),
    icon: Users,
    color: "bg-purple-100 text-purple-700",
    description: t("programs.doula.description", "An end-of-life doula guides a person who is transitioning to death and their loved ones through the dying process. Doulas help families cope with death by recognizing it as a natural and important part of life."),
    quote: t("programs.doula.quote", "An end-of-life doula guides a person who is transitioning to death and their loved ones through the dying process."),
    quoteAuthor: t("programs.doula.quoteAuthor", "The International End-of-Life Doula Association (INELDA)"),
    benefits: [
      t("programs.doula.benefits.1", "Emotional, spiritual, and physical support at an intensely personal time"),
      t("programs.doula.benefits.2", "Assists with planning how last days will unfold"),
      t("programs.doula.benefits.3", "Supports loved ones through the dying process"),
      t("programs.doula.benefits.4", "Eases the suffering of grief"),
      t("programs.doula.benefits.5", "Makes transition less chaotic and more focused on compassion"),
      t("programs.doula.benefits.6", "Sits with dying loved ones or guides through meditation")
    ],
    details: t("programs.doula.details", "Doulas are considered part of the interdisciplinary team at Goodwin Hospice. They fill in as needed at the family's request, providing presence, guidance, and support."),
    highlight: t("programs.doula.highlight", "Provided through generous donor support"),
    phone: "703.578.7108",
    website: "https://GoodwinHospice.org"
  },
  {
    id: "aftercare",
    title: t("programs.aftercare.title", "Full Circle Aftercare"),
    subtitle: t("programs.aftercare.subtitle", "We Can Be Your Guide"),
    icon: FileText,
    color: "bg-teal-100 text-teal-700",
    description: t("programs.aftercare.description", "The loss of a loved one is difficult. You may not know what to do next. We provide resources to help guide you through the complexities of wrapping up your loved one's estate."),
    services: [
      { category: t("programs.aftercare.services.government", "Government"), items: [t("programs.aftercare.services.socialSecurity", "Social Security"), t("programs.aftercare.services.va", "Veterans Administration"), t("programs.aftercare.services.opm", "OPM")] },
      { category: t("programs.aftercare.services.finances", "Finances"), items: [t("programs.aftercare.services.pension", "Pension Plans"), t("programs.aftercare.services.401k", "401K/IRA"), t("programs.aftercare.services.creditCards", "Credit Cards"), t("programs.aftercare.services.bankAccounts", "Bank Accounts")] },
      { category: t("programs.aftercare.services.insurance", "Insurance"), items: [t("programs.aftercare.services.lifeInsurance", "Life Insurance"), t("programs.aftercare.services.medical", "Medical/Health"), t("programs.aftercare.services.carHome", "Car/Home"), t("programs.aftercare.services.beneficiaries", "Change Beneficiaries")] },
      { category: t("programs.aftercare.services.estate", "Estate"), items: [t("programs.aftercare.services.wills", "Wills/Trusts/Probate"), t("programs.aftercare.services.realEstate", "Real Estate Title"), t("programs.aftercare.services.vehicle", "Vehicle Title")] },
      { category: t("programs.aftercare.services.protection", "Protection"), items: [t("programs.aftercare.services.creditBureau", "Credit Bureau/Fraud Alert"), t("programs.aftercare.services.deceased", "Deceased Registry"), t("programs.aftercare.services.junkMail", "Stop Junk Mail"), t("programs.aftercare.services.subscriptions", "Cancel Subscriptions")] }
    ],
    details: t("programs.aftercare.details", "Further assistance from a trained estate specialist is available to help you navigate this difficult time."),
    highlight: t("programs.aftercare.highlight", "Expert guidance when you need it most"),
    phone: "434.933.6852",
    website: "https://GoodwinHospice.org"
  }
];

export default function Programs() {
  const { t } = useTranslation();
  
  useEffect(() => {
    recordPageVisit("programs").catch(console.error);
  }, []);

  const programs = getPrograms(t);

  const handleCall = (phone) => {
    window.location.href = `tel:${phone.replace(/[^0-9]/g, '')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/50 to-background" data-testid="programs-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-teal-50 to-background">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <Badge variant="secondary" className="mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              {t("programs.hero.badge", "Beyond Traditional Care")}
            </Badge>
            <h1 className="heading-1 text-foreground">
              {t("programs.hero.title", "Complementary")} <span className="gradient-text">{t("programs.hero.titleHighlight", "Programs")}</span>
            </h1>
            <p className="body-lg text-muted-foreground">
              {t("programs.hero.description", "At Goodwin Hospice, our care is rooted in the belief that every person has the right to die pain-free and with dignity. We offer therapies and services that go above and beyond traditional hospice care.")}
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="section-padding">
        <div className="container-max space-y-12">
          {programs.map((program, index) => {
            const Icon = program.icon;
            return (
              <Card key={program.id} className="glass-card border-0 overflow-hidden" data-testid={`program-${program.id}`}>
                <div className="grid lg:grid-cols-3 gap-0">
                  {/* Left Column - Main Info */}
                  <div className="lg:col-span-2 p-8">
                    <CardHeader className="p-0 mb-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`w-14 h-14 rounded-2xl ${program.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-7 h-7" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-serif">{program.title}</CardTitle>
                          <CardDescription className="text-base mt-1">{program.subtitle}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="p-0 space-y-6">
                      <p className="text-muted-foreground leading-relaxed">{program.description}</p>
                      
                      {program.quote && (
                        <blockquote className="border-l-4 border-primary/30 pl-4 py-2 italic text-muted-foreground bg-primary/5 rounded-r-lg">
                          <p>"{program.quote}"</p>
                          <footer className="text-sm mt-2 font-medium text-foreground">— {program.quoteAuthor}</footer>
                        </blockquote>
                      )}

                      {program.benefits && (
                        <div>
                          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                            <Heart className="w-4 h-4 text-primary" />
                            {t("programs.benefits", "Benefits")}
                          </h4>
                          <ul className="grid sm:grid-cols-2 gap-2">
                            {program.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {program.services && (
                        <div>
                          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-primary" />
                            {t("programs.servicesWeHelp", "Services We Help With")}
                          </h4>
                          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {program.services.map((service, i) => (
                              <div key={i} className="bg-muted/50 rounded-lg p-3">
                                <h5 className="font-medium text-sm text-foreground mb-2">{service.category}</h5>
                                <ul className="space-y-1">
                                  {service.items.map((item, j) => (
                                    <li key={j} className="text-xs text-muted-foreground flex items-center gap-1">
                                      <span className="w-1 h-1 rounded-full bg-primary" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <p className="text-sm text-muted-foreground">{program.details}</p>
                    </CardContent>
                  </div>

                  {/* Right Column - CTA */}
                  <div className="bg-primary/5 p-8 flex flex-col justify-center border-l border-border/50">
                    <div className="space-y-6">
                      <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {program.highlight}
                      </Badge>
                      
                      <div className="space-y-3">
                        <Button 
                          className="w-full rounded-full bg-primary hover:bg-primary/90"
                          onClick={() => handleCall(program.phone)}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          {t("programs.call", "Call")} {program.phone}
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          className="w-full rounded-full"
                          onClick={() => window.open(program.website, '_blank')}
                        >
                          <Globe className="w-4 h-4 mr-2" />
                          {t("programs.visitWebsite", "Visit Website")}
                          <ExternalLink className="w-3 h-3 ml-2" />
                        </Button>
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        {t("programs.contactToLearnMore", "Contact us to learn more about this program")}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="heading-2 text-white">{t("programs.cta.title", "Have Questions About Our Programs?")}</h2>
            <p className="body-lg text-white/80">
              {t("programs.cta.description", "Our team is here to help you understand how these complementary programs can support you and your loved ones during this journey.")}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg"
                onClick={() => handleCall("703.578.7108")}
              >
                <Phone className="w-5 h-5 mr-2" />
                {t("programs.cta.callButton", "Call 703.578.7108")}
              </Button>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                {t("programs.cta.contactOnline", "Contact Us Online")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
